import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, ArrowRight, Star, Music2, Heart, Clock, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSubdomain, getMainDomainUrl } from '../utils/conventionService';
import { fetchWithAuth, SEASON_EVENTS_API } from '../utils/api';
import ddLogo from './ui/dd.png';

// Dance Particles for Convention Landing
const ConventionDanceParticles = () => {
  const createParticle = (index) => {
    const baseX = Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200);
    const baseY = Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800);
    
    return { startX: baseX, startY: baseY };
  };

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
      {[...Array(20)].map((_, i) => {
        const { startX, startY } = createParticle(i);
        
        return (
          <motion.div
            key={i}
            className="absolute"
            initial={{
              x: startX,
              y: startY,
              scale: 0,
              rotate: 0
            }}
            animate={{
              y: [null, startY - 300, startY - 600],
              x: [
                null, 
                startX + (Math.random() * 150 - 75),
                startX + (Math.random() * 200 - 100)
              ],
              scale: [0, 1, 0],
              rotate: [0, 360, 720],
              opacity: [0, 0.6, 0]
            }}
            transition={{
              duration: Math.random() * 6 + 8,
              repeat: Infinity,
              delay: Math.random() * 8,
              ease: "easeInOut"
            }}
          >
            {i % 4 === 0 && <Star className="w-4 h-4 text-pink-400 drop-shadow-lg" />}
            {i % 4 === 1 && <Heart className="w-4 h-4 text-purple-400 drop-shadow-lg" />}
            {i % 4 === 2 && <Music2 className="w-4 h-4 text-orange-400 drop-shadow-lg" />}
            {i % 4 === 3 && <div className="w-3 h-3 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full drop-shadow-lg" />}
          </motion.div>
        );
      })}
    </div>
  );
};

// Convention Background
const ConventionBackground = () => {
  return (
    <div className="fixed inset-0 -z-10">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900" />
      
      {/* Animated spotlight effects */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            "radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.4) 0%, transparent 50%)",
            "radial-gradient(circle at 80% 50%, rgba(168, 85, 247, 0.5) 0%, transparent 50%)",
            "radial-gradient(circle at 50% 20%, rgba(251, 146, 60, 0.4) 0%, transparent 50%)",
            "radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.4) 0%, transparent 50%)"
          ]
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
      />
      
      {/* Stage floor reflection */}
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
    </div>
  );
};

const ConventionLanding = () => {
  const [conventionData, setConventionData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const conventionSubdomain = getSubdomain();

  // Helper function to create auth URLs with convention parameter
  const getAuthUrl = (state) => {
    const mainDomain = getMainDomainUrl();
    const conventionParam = conventionSubdomain ? `&convention=${conventionSubdomain}` : '';
    return `${mainDomain}/auth?state=${state}${conventionParam}`;
  };

  useEffect(() => {
    // Fetch convention-specific data
    const fetchConventionData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Default convention info structure
        let conventionInfo = {
          name: conventionSubdomain ? 
            `${conventionSubdomain.charAt(0).toUpperCase() + conventionSubdomain.slice(1)} Dance Convention` : 
            'Dance Convention',
          description: 'Join us for an incredible dance experience with world-class instructors, competitions, and networking opportunities.',
          logo: null,
        };
        
        // Try to get real convention info from the public endpoint
        try {
          const conventionInfoResponse = await fetch(`https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/public/convention-info?subdomain=${conventionSubdomain}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Origin': window.location.origin
            }
          });
          
          if (conventionInfoResponse.ok) {
            const conventionData = await conventionInfoResponse.json();
            
            // Check if the convention actually exists
            if (conventionData.exists && conventionData.conventionName) {
              conventionInfo = {
                name: conventionData.conventionName,
                description: conventionData.conventionDescription || conventionInfo.description,
                logo: conventionData.logo || conventionInfo.logo,
              };
            } else {
              console.log('Convention subdomain is available but not registered');
              // Keep the default info since the convention doesn't exist yet
            }
          } else {
            console.log('Failed to fetch convention info');
          }
        } catch (conventionError) {
          console.log('Could not fetch convention info:', conventionError.message);
        }
        
        setConventionData(conventionInfo);
        // No events unless we have a real API that works
        setEvents([]);
        
        // Try to fetch events for this convention
        try {
          const eventsResponse = await fetch(`https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/public/convention-events?subdomain=${conventionSubdomain}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Origin': window.location.origin
            }
          });
          
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            console.log('Events data received:', eventsData);
            
            if (eventsData.events && Array.isArray(eventsData.events)) {
              setEvents(eventsData.events);
              console.log(`Loaded ${eventsData.events.length} events for convention`);
            }
          } else {
            console.log('Failed to fetch convention events');
          }
        } catch (eventsError) {
          console.log('Could not fetch convention events:', eventsError.message);
        }
        
      } catch (error) {
        console.error('Error fetching convention data:', error);
        setError('Unable to load convention information at this time');
        
        // Fallback to basic convention data without events
        const fallbackConventionData = {
          name: conventionSubdomain ? 
            `${conventionSubdomain.charAt(0).toUpperCase() + conventionSubdomain.slice(1)} Dance Convention` : 
            'Dance Convention',
          description: 'Join us for an incredible dance experience with world-class instructors, competitions, and networking opportunities.',
          logo: null,
        };
        setConventionData(fallbackConventionData);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchConventionData();
  }, [conventionSubdomain]);

  // Parse date string to Date object, handling MM-DD-YYYY format
  const parseDate = (dateString) => {
    if (!dateString) return new Date(0);
    
    // Handle MM-DD-YYYY format properly
    if (dateString.includes('-') && dateString.length === 10 && dateString.indexOf('-') === 2) {
      // MM-DD-YYYY format - convert to YYYY-MM-DD for reliable parsing
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      }
    }
    
    return new Date(dateString);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "TBA";
    const date = parseDate(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "TBA";
    
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return "Invalid Date";
    }
    
    if (start.toDateString() === end.toDateString()) {
      return formatDate(startDate);
    }
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const isEventSoon = (startDate) => {
    if (!startDate) return false;
    const eventDate = parseDate(startDate);
    
    // Check if date is valid
    if (isNaN(eventDate.getTime())) {
      return false;
    }
    
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0; // Event is within 30 days
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-pink-400/30 border-t-pink-400 rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <ConventionBackground />
      <ConventionDanceParticles />

      {/* Header */}
      <motion.div 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed top-0 w-full bg-black/80 backdrop-blur-2xl border-b border-pink-500/30 z-50"
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <motion.div 
                whileHover={{ scale: 1.1, rotate: 10 }}
                className="relative"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center border border-pink-400/50 shadow-lg shadow-pink-500/25">
                  <img 
                    src={conventionData?.logo || ddLogo}
                    alt={conventionData?.name}
                    className="w-7 h-7 object-contain filter brightness-0 invert"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl blur-lg opacity-60 transition-opacity" />
              </motion.div>
              <span className="text-2xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent">
                {conventionData?.name}
              </span>
            </div>
            
            <a 
              href={getAuthUrl('signIn')}
              className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 font-semibold"
            >
              Sign In
            </a>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="pt-24 pb-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="container mx-auto px-6 py-16 text-center"
        >
          <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent mb-6">
            Welcome to {conventionData?.name}
          </h1>
          <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-8">
            {conventionData?.description}
          </p>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <a
              href={getAuthUrl('signUp')}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-pink-500/30 transition-all duration-300 font-bold text-lg"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </motion.div>
        </motion.div>

        {/* Events Section - List Format */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="container mx-auto px-6 py-16"
        >
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-pink-300 to-purple-300 bg-clip-text text-transparent">
            Upcoming Events
          </h2>
          
          {error && (
            <div className="bg-red-500/10 border border-red-400/30 rounded-2xl p-4 mb-8 text-center">
              <p className="text-red-300">Unable to load events. Please try again later.</p>
            </div>
          )}
          
          <div className="max-w-4xl mx-auto">
            {events.length === 0 ? (
              <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-3xl p-8 text-center">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-pink-400/50" />
                <h3 className="text-2xl font-bold text-white mb-2">No Upcoming Events</h3>
                <p className="text-white/60">
                  {error ? 'We\'re having trouble loading events right now.' : 'Check back soon for exciting new events!'}
                </p>
              </div>
            ) : (
              <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-3xl overflow-hidden">
                {events.map((event, index) => (
                  <motion.div
                    key={event.eventId || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    className={`p-6 border-b border-white/10 last:border-b-0 hover:bg-white/5 transition-all duration-300 ${
                      isEventSoon(event.startDate) ? 'bg-pink-500/5' : ''
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-white">
                            {event.name}
                          </h3>
                          
                          {event.eventType && (
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-400/30">
                              {event.eventType}
                            </span>
                          )}
                          {isEventSoon(event.startDate) && (
                            <span className="px-2 py-1 bg-orange-500/20 text-orange-300 text-xs rounded-full border border-orange-400/30 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Soon
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-white/70 text-sm">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-pink-400" />
                            <span>{formatDateRange(event.startDate, event.endDate)}</span>
                          </div>
                          
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-2 text-purple-400" />
                            <span>{event.city || 'Location TBA'}</span>
                          </div>

                          {event.season && (
                            <div className="flex items-center">
                              <Star className="w-4 h-4 mr-2 text-orange-400" />
                              <span>Season: {event.season}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {event.status === 'past' ? (
                          <span className="px-4 py-2 text-gray-400 text-sm font-medium">
                            Passed
                          </span>
                        ) : (
                          <a
                            href={getAuthUrl('signUp')}
                            className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 font-semibold text-sm whitespace-nowrap"
                          >
                            {event.status === 'ongoing' ? 'Join Now' : 'Register Now'}
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="container mx-auto px-6 py-16 text-center"
        >
          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-3xl p-8 max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Join Us?
            </h3>
            <p className="text-white/70 mb-6">
              Create your account to register for events and access exclusive content.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={getAuthUrl('signUp')}
                className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 font-semibold"
              >
                Create Account
              </a>
              <a
                href={getAuthUrl('signIn')}
                className="px-6 py-3 bg-white/10 backdrop-blur-xl border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all duration-300 font-semibold"
              >
                Sign In
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ConventionLanding; 